/**
 * Check the balance by hand (TMMAPP-0132). Undrawn: the prototype's 460 dialog. The last checks
 * sit under the form; there is no separate history page.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { accountsApi, type Account, type BalanceCheck } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import { GuardedDialog } from '@/components/GuardedDialog';
import { MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';

interface CheckBalanceDialogProps {
  account: Account;
  /** Today's civil date in the viewer's zone. The On field starts here. */
  today: string;
  onClose: () => void;
  /** The posted check. The caller refetches the account and closes this dialog. */
  onRecorded: (check: BalanceCheck) => void;
}

function statusWord(t: (key: string) => string, status: string): string {
  switch (status) {
    case 'BALANCED':
      return t('accounts.balanceCheck.statusBalanced');
    case 'ADJUSTED':
      return t('accounts.balanceCheck.statusAdjusted');
    case 'OPENING_FIXED':
      return t('accounts.balanceCheck.statusOpeningFixed');
    case 'DISMISSED':
      return t('accounts.balanceCheck.statusDismissed');
    case 'SUPERSEDED':
      return t('accounts.balanceCheck.statusSuperseded');
    case 'OPEN':
      return t('accounts.balanceCheck.statusOpen');
    default:
      return status;
  }
}

export function CheckBalanceDialog({
  account,
  today,
  onClose,
  onRecorded,
}: CheckBalanceDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const fullScreen = useDialogFullScreen();
  const [amount, setAmount] = useState('');
  const [on, setOn] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [history, setHistory] = useState<
    { status: 'loading' } | { status: 'ready'; items: BalanceCheck[] } | { status: 'error' }
  >({ status: 'loading' });
  const { dirty } = useDirty(`${amount}|${on}`);
  const guard = useDialogDirtyGuard(dirty, onClose, busy);
  const currency = account.currency;
  const openingOn = account.openingBalanceOn;

  useEffect(() => {
    let cancelled = false;
    void accountsApi
      .listBalanceChecks(account.id, { limit: 20 })
      .then((page) => {
        if (!cancelled) setHistory({ status: 'ready', items: page.items });
      })
      .catch(() => {
        if (!cancelled) setHistory({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [account.id]);

  async function handleCheck() {
    if (busy) return;
    const minor = parseMoneyToMinor(amount, currency, locale);
    let dateMessage: string | null = null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(on)) dateMessage = t('accounts.balanceCheck.dateRequired');
    else if (on > today) dateMessage = t('errors.server.notFuture');
    else if (openingOn !== null && on < openingOn) {
      dateMessage = t('accounts.balanceCheck.onAfterOpening', {
        date: formatCivilDate(openingOn, locale),
      });
    }
    setAmountError(minor === null);
    setDateError(dateMessage);
    if (minor === null || dateMessage !== null) return;
    setBusy(true);
    setError(null);
    try {
      const check = await accountsApi.recordBalanceCheck(account.id, {
        bankBalanceMinor: minor,
        on,
      });
      onRecorded(check);
    } catch (err) {
      setError(errorCopy(t, err, 'accounts.errorGeneric'));
      setBusy(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      fullWidth
      fullScreen={fullScreen}
      slotProps={{
        paper: { sx: { maxWidth: '100%', ...(fullScreen ? {} : { width: 460 }) } },
      }}
    >
      <DialogTitle>{t('accounts.balanceCheck.checkTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error !== null && (
            <Typography variant="body2" role="alert" color="error">
              {error}
            </Typography>
          )}
          <TextField
            label={t('accounts.balanceCheck.bankBalance')}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setAmountError(false);
            }}
            required
            fullWidth
            error={amountError}
            helperText={
              amountError
                ? t('accounts.balanceCheck.amountRequired')
                : t('accounts.balanceCheck.amountHelp')
            }
          />
          <TextField
            type="date"
            label={t('accounts.balanceCheck.on')}
            value={on}
            onChange={(event) => {
              setOn(event.target.value);
              setDateError(null);
            }}
            required
            fullWidth
            error={dateError !== null}
            helperText={dateError}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
          />
          <Stack spacing={1} component="section" aria-label={t('accounts.balanceCheck.history')}>
            <Typography variant="h3" component="h3">
              {t('accounts.balanceCheck.history')}
            </Typography>
            {history.status === 'loading' && (
              <Typography variant="body2">{t('common.loading')}</Typography>
            )}
            {history.status === 'error' && (
              <Typography variant="body2">{t('accounts.errorGeneric')}</Typography>
            )}
            {history.status === 'ready' && history.items.length === 0 && (
              <Typography variant="body2">{t('accounts.balanceCheck.historyEmpty')}</Typography>
            )}
            {history.status === 'ready' &&
              history.items.map((check) => (
                <Typography key={check.id} variant="body2">
                  <Trans
                    i18nKey="accounts.balanceCheck.historyRow"
                    values={{
                      date: formatCivilDate(check.on, locale),
                      status: statusWord(t, check.status),
                    }}
                    components={{
                      bank: (
                        <MoneyText
                          minor={check.bankBalanceMinor}
                          currency={currency}
                          variant="body2"
                        />
                      ),
                      ledger: (
                        <MoneyText
                          minor={check.ledgerBalanceMinor}
                          currency={currency}
                          variant="body2"
                        />
                      ),
                    }}
                  />
                </Typography>
              ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <SubmitButton loading={busy} onClick={() => void handleCheck()}>
          {t('accounts.balanceCheck.check')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
